import type { ContactDetailsUpdated } from '../control-events.js';
import { GeminiContactDetailsSchema } from '@merlin/project-schema/live-session';

export interface ContactDetailsConfirmation {
  revision: number;
  consent: true;
  data: { name: string; email: string; phone?: string; organisation: string };
}

const FIELDS = ['name', 'email', 'phone', 'organisation'] as const;
type Field = typeof FIELDS[number];

/** Session-only, nonmodal review of spoken details. Only the caller can confirm. */
export class ContactDetailsCard {
  private currentRevision = -1;
  private readonly dirty = new Set<Field>();
  private waiting = false;
  private confirmed = false;
  private submittedRevision?: number;
  private awaitingRevision = false;
  /** Latches true once review has been requested; never resets except on clear(). */
  private reviewRequested = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly onSubmit: (value: ContactDetailsConfirmation) => void,
    private readonly onEdit: (revisionToRevoke: number | undefined) => void,
  ) {
    root.classList.add('intake-contact-card');
    root.setAttribute('aria-label', 'Check your contact details');
    root.hidden = true;
  }

  get revision(): number { return this.currentRevision; }

  open(): void {
    this.reviewRequested = true;
    if (this.currentRevision < 0) this.apply({ revision: 0, details: {}, confirmed: false, reviewRequested: false });
    this.root.hidden = false;
    this.refresh();
    this.input('name').focus();
  }

  apply(update: ContactDetailsUpdated): void {
    if (update.revision <= this.currentRevision) return;
    if (this.currentRevision < 0) this.render();
    this.currentRevision = update.revision;
    this.waiting = false;
    this.confirmed = false;
    this.submittedRevision = undefined;
    this.awaitingRevision = false;
    for (const name of FIELDS) {
      const input = this.input(name);
      if (!this.dirty.has(name)) input.value = update.details[name] ?? '';
    }
    if (update.reviewRequested) this.reviewRequested = true;
    const hasCapturedDetail = FIELDS.some(name => (update.details[name] ?? '').trim() !== '');
    if (this.reviewRequested || hasCapturedDetail) this.root.hidden = false;
    this.feedback(this.reviewRequested
      ? 'Check the details as Merlin writes them. You can correct anything here.'
      : 'Merlin will ask you to check these before you confirm.');
    this.refresh();
  }

  accept(revision: number): boolean {
    if (!this.waiting || this.awaitingRevision || revision !== this.currentRevision) return false;
    this.waiting = false;
    this.confirmed = true;
    this.dirty.clear();
    this.feedback('Details confirmed. Your playbook can be prepared. Tell Merlin when you’re ready for team follow-up.');
    this.refresh();
    return true;
  }

  reject(message: string): void {
    this.waiting = false;
    this.confirmed = false;
    this.feedback(message, true);
    this.refresh();
  }

  clear(): void {
    this.root.hidden = true;
    this.root.innerHTML = '';
    this.root.removeAttribute('aria-busy');
    this.currentRevision = -1;
    this.dirty.clear();
    this.waiting = this.confirmed = false;
    this.submittedRevision = undefined;
    this.awaitingRevision = false;
    this.reviewRequested = false;
  }

  private input(name: Field): HTMLInputElement {
    return this.root.querySelector<HTMLInputElement>(`input[name="${name}"]`)!;
  }

  private read(): ContactDetailsConfirmation['data'] {
    return {
      name: this.input('name').value,
      email: this.input('email').value,
      ...(this.input('phone').value.trim() ? { phone: this.input('phone').value } : {}),
      organisation: this.input('organisation').value,
    };
  }

  private valid(data: ContactDetailsConfirmation['data']): boolean {
    return GeminiContactDetailsSchema.safeParse(data).success;
  }

  private feedback(message: string, error = false): void {
    const node = this.root.querySelector<HTMLElement>('.intake-contact-feedback');
    if (!node) return;
    node.setAttribute('role', error ? 'alert' : 'status');
    node.textContent = message;
  }

  private refresh(): void {
    const button = this.root.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!button) return;
    this.root.setAttribute('aria-busy', String(this.waiting || this.awaitingRevision));
    button.disabled = !this.reviewRequested || this.waiting || this.awaitingRevision || this.confirmed || !this.valid(this.read());
    button.textContent = this.awaitingRevision ? 'Updating your details…' : this.waiting ? 'Confirming…' : this.confirmed ? 'Details confirmed' : 'Confirm details & request follow-up';
  }

  private render(): void {
    this.root.innerHTML = `
      <form novalidate>
        <h2>Check Merlin’s notes</h2>
        <p class="intake-contact-purpose" id="intake-contact-purpose">Confirm your name, business or project and email to unlock your playbook download and ask the Head Artificer to follow up by the next business day. You authorise Merlin to email your details, questions and playbook to <a href="mailto:headartificer@artificersai.com">headartificer@artificersai.com</a>. Phone is optional.</p>
        <div class="intake-contact-fields">
          <label><span>Full name</span><input name="name" type="text" autocomplete="name" maxlength="120" required spellcheck="false" /></label>
          <label><span>Email</span><input name="email" type="email" autocomplete="email" maxlength="254" required spellcheck="false" /></label>
          <label><span>Phone (optional)</span><input name="phone" type="tel" autocomplete="tel" maxlength="40" /></label>
          <label><span>Business or project name</span><input name="organisation" type="text" autocomplete="organization" maxlength="160" required spellcheck="false" /></label>
        </div>
        <p class="intake-contact-feedback" id="intake-contact-feedback" role="status" aria-live="polite"></p>
        <button type="submit" aria-describedby="intake-contact-purpose intake-contact-feedback" disabled>Confirm details &amp; request follow-up</button>
      </form>`;
    for (const name of FIELDS) {
      this.input(name).setAttribute('aria-describedby', 'intake-contact-feedback');
      this.input(name).addEventListener('input', () => {
        this.dirty.add(name);
        const revoke = this.submittedRevision === this.currentRevision && !this.awaitingRevision;
        if (revoke) this.awaitingRevision = true;
        this.waiting = this.confirmed = false;
        this.feedback(this.awaitingRevision
          ? 'Your edits are kept here. Waiting for Merlin to refresh the confirmation…'
          : this.reviewRequested
            ? 'Confirm your updated details to prepare your playbook.'
            : 'Merlin will ask you to check these before you confirm.');
        this.onEdit(revoke ? this.currentRevision : undefined);
        this.refresh();
      });
    }
    this.root.querySelector('form')!.addEventListener('submit', event => {
      event.preventDefault();
      const parsed = GeminiContactDetailsSchema.safeParse(this.read());
      if (this.root.hidden || !this.reviewRequested || this.waiting || this.awaitingRevision || this.confirmed || !parsed.success) return;
      this.waiting = true;
      this.submittedRevision = this.currentRevision;
      this.feedback('Checking your confirmation…');
      this.refresh();
      this.onSubmit({ revision: this.currentRevision, consent: true, data: parsed.data });
    });
  }
}
